import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  FirestoreError,
  runTransaction
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { EarnedAchievement } from '@/models/Achievement';

// Interface for user profile data
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  joinedAt: any;
  lastLoginAt: any;
  streak: number;
  wordsLearned: number;
  xp: number;
  level: number;
  admin?: boolean;
  achievements?: EarnedAchievement[];  
  completedLessons?: Record<string, any>;
  xpToAdd?: number; // Add this field to support XP updates
}

class ProfileService {
  private collection = 'userProfiles';

  // Get a user profile by ID
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, this.collection, userId);
      const snapshot = await getDoc(docRef);
      
      if (snapshot.exists()) {
        return snapshot.data() as UserProfile;
      }
      
      return null;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error fetching user profile:', firestoreError);
      toast.error('Failed to load user profile');
      return null;
    }
  }

  // Create a new user profile
  async createUserProfile(user: { id: string; email: string; name: string }): Promise<UserProfile> {
    try {
      const newProfile: UserProfile = {
        id: user.id,
        email: user.email,
        name: user.name,
        joinedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        streak: 0,
        wordsLearned: 0,
        xp: 0,
        level: 1,
        achievements: []  // Initialize with empty achievements array
      };
      
      await setDoc(doc(db, this.collection, user.id), newProfile);
      
      return newProfile;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error creating user profile:', firestoreError);
      toast.error('Failed to create user profile');
      throw error;
    }
  }

  // Update a user profile
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<boolean> {
    try {
      const userRef = doc(db, this.collection, userId);
      
      await updateDoc(userRef, {
        ...updates,
        lastLoginAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error updating user profile:', firestoreError);
      toast.error('Failed to update user profile');
      return false;
    }
  }

  // Update last login timestamp
  async updateLastLogin(userId: string): Promise<void> {
    try {
      const userRef = doc(db, this.collection, userId);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last login:', error);
      // No toast here as this is a background operation
    }
  }

  // Add a new achievement to a user's profile
  async addAchievement(userId: string, achievement: EarnedAchievement): Promise<boolean> {
    try {
      console.log('🏆 [ProfileService] Adding achievement to user profile:', userId, achievement.id);
      
      // First, check if the achievement already exists by getting the user profile directly
      const userRef = doc(db, this.collection, userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.error('❌ [ProfileService] Cannot add achievement: User profile not found');
        return false;
      }
      
      const userData = userSnap.data();
      
      // Ensure achievements is always an array
      let existingAchievements = [];
      if (userData.achievements) {
        // Handle both array and object formats
        if (Array.isArray(userData.achievements)) {
          existingAchievements = userData.achievements;
        } else {
          // Convert from Firestore object format to array
          existingAchievements = Object.values(userData.achievements);
        }
      }
      
      console.log('🏆 [ProfileService] Current achievements:', existingAchievements);
      
      // Check if this achievement ID is already earned
      const alreadyEarned = existingAchievements.some(a => a.id === achievement.id);
      
      if (alreadyEarned) {
        console.log('🏆 [ProfileService] Achievement already exists, skipping:', achievement.id);
        return true; // Return true since it's already in the profile
      }
      
      // Instead of using property path, set the entire achievements array
      await updateDoc(userRef, {
        achievements: [...existingAchievements, achievement]
      });
      
      console.log('🏆 [ProfileService] Achievement added successfully:', achievement.id);
      return true;
      
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('❌ Error adding achievement:', firestoreError);
      toast.error('Failed to add achievement');
      return false;
    }
  }

  // Get all unviewed achievements for a user
  async getUnviewedAchievements(userId: string): Promise<string[]> {
    try {
      console.log('🏆 [ProfileService] Getting unviewed achievements for user:', userId);
      const userRef = doc(db, this.collection, userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.log('🏆 [ProfileService] No user profile found');
        return [];
      }
      
      const userData = userSnap.data();
      if (!userData.achievements) {
        console.log('🏆 [ProfileService] No achievements found for user');
        return [];
      }
      
      // Handle both array and object formats
      let achievementsArray = [];
      if (Array.isArray(userData.achievements)) {
        achievementsArray = userData.achievements;
      } else {
        // Convert from Firestore object format to array
        achievementsArray = Object.values(userData.achievements);
      }
      
      console.log('🏆 [ProfileService] Processing achievements:', achievementsArray);
      
      // Filter for unviewed achievements
      const unviewedAchievements = achievementsArray
        .filter(achievement => achievement.viewed === false)
        .map(achievement => achievement.id);
      
      console.log(`🏆 [ProfileService] Found ${unviewedAchievements.length} unviewed achievements`);
      return unviewedAchievements;
    } catch (error) {
      console.error('❌ [ProfileService] Error getting unviewed achievements:', error);
      return [];
    }
  }

  // Mark an achievement as viewed in the user's profile
  async markAchievementAsViewed(userId: string, achievementId: string): Promise<boolean> {
    try {
      console.log('🏆 [ProfileService] Marking achievement as viewed:', userId, achievementId);
      const userRef = doc(db, this.collection, userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.error('❌ [ProfileService] Cannot mark achievement as viewed: User profile not found');
        return false;
      }
      
      const userData = userSnap.data();
      if (!userData.achievements) {
        console.error('❌ [ProfileService] Cannot mark achievement as viewed: No achievements found');
        return false;
      }
      
      // Handle both array and object formats
      let achievementsArray = [];
      if (Array.isArray(userData.achievements)) {
        achievementsArray = userData.achievements;
      } else {
        // Convert from Firestore object format to array
        achievementsArray = Object.values(userData.achievements);
      }
      
      // Find the achievement in the user's profile
      const updatedAchievements = achievementsArray.map(achievement => {
        if (achievement.id === achievementId) {
          console.log('🏆 [ProfileService] Found achievement to mark as viewed:', achievement.id);
          return { ...achievement, viewed: true };
        }
        return achievement;
      });
      
      // Update the user's profile with the modified achievements array
      await updateDoc(userRef, {
        achievements: updatedAchievements
      });
      
      console.log('🏆 [ProfileService] Achievement marked as viewed successfully');
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('❌ Error marking achievement as viewed:', firestoreError);
      return false;
    }
  }

  // Record a lesson completion in the user's profile
  async recordLessonCompletion(userId: string, lessonId: string): Promise<boolean> {
    try {
      console.debug('📝 Recording lesson completion in profile:', userId, lessonId);
      const userRef = doc(db, this.collection, userId);
      
      // Create a field update that adds/updates this specific lessonId in the completedLessons map
      // Using dot notation to update a specific field in the map
      await updateDoc(userRef, {
        [`completedLessons.${lessonId}`]: serverTimestamp()
      });
      
      console.debug('📝 Lesson completion recorded successfully');
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('❌ Error recording lesson completion:', firestoreError);
      return false;
    }
  }
}

export const profileService = new ProfileService();
